import { createFileRoute, Link } from "@tanstack/react-router";
import { Check, Building2, Church, Truck } from "lucide-react";
import { Reveal } from "@/components/site-chrome";
import { RequestService } from "@/components/request-service";
import { PageHero, Eyebrow, HowItWorks, FinalCta } from "@/components/sections";
import { pageHead, breadcrumbLd } from "@/lib/seo";

const TITLE = "Fleet Vehicle Maintenance Great Falls MT | 406 Mobile Mechanic";
const DESC = "On-location fleet maintenance in Great Falls, MT for businesses, churches and organizations — less downtime, done at your lot. Call or text 406-799-0095.";

const work = [
  "Scheduled maintenance — oil changes, filters and fluid service",
  "Diagnostics and check engine lights",
  "Brake and suspension repair",
  "Electrical, battery and no-start problems",
  "Heating and AC repair",
  "Pre-purchase inspections for vehicles you're adding to the fleet",
];

export const Route = createFileRoute("/fleet-service")({
  head: () => pageHead("/fleet-service", TITLE, DESC, [breadcrumbLd([{ name: "Home", path: "/" }, { name: "Fleet Service", path: "/fleet-service" }])]),
  component: FleetPage,
});

function FleetPage() {
  return (
    <main>
      <PageHero
        crumbs={[{ label: "Home", to: "/" }, { label: "Fleet Service" }]}
        eyebrow="Businesses · Churches · Organizations"
        title={<>Fleet Vehicle Maintenance in <span className="text-primary">Great Falls, MT</span></>}
        intro="Keep your business vehicles on the road — fleet service available. Josh services your vehicles at your location, so they spend less time off the road and your people stay on the job."
        photo="truck"
      />
      <section className="bg-background py-24">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-2">
          <Reveal>
            <Eyebrow>On location</Eyebrow>
            <h2 className="mt-4 text-4xl font-black md:text-5xl">Fleet Service Without the Downtime</h2>
            <p className="mt-6 text-lg text-foreground/85">No shuttling vehicles to a shop and waiting days to get them back. The work happens in your lot, between shifts or while your team is busy with something else.</p>
            <ul className="mt-8 grid gap-4 sm:grid-cols-3">
              {[{ i: Building2, t: "Local businesses" }, { i: Church, t: "Churches" }, { i: Truck, t: "Organizations & work trucks" }].map((a) => (
                <li key={a.t} className="rounded-sm border border-border bg-card p-4 text-center"><a.i className="mx-auto h-7 w-7 text-primary" aria-hidden /><p className="mt-2 font-display text-lg font-bold uppercase">{a.t}</p></li>
              ))}
            </ul>
          </Reveal>
          <Reveal className="cut-corner border-2 border-dashed border-steel/50 bg-card p-8">
            <p className="label-mono text-steel">Fleet work</p>
            <h2 className="mt-2 text-4xl font-black">What We Handle</h2>
            <ul className="mt-6 space-y-3">
              {work.map((w) => <li key={w} className="flex items-start gap-3"><Check className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden />{w}</li>)}
            </ul>
            <p className="mt-6 text-sm text-muted-foreground">See every <Link to="/services" className="text-primary hover:underline">service we offer</Link>.</p>
          </Reveal>
        </div>
      </section>
      <HowItWorks heading="How Fleet Service Works" />
      <FinalCta title={<>Talk fleet service. <span className="text-primary">Call or text Josh.</span></>} />
      <RequestService defaultService="Fleet Service" contactLink />
    </main>
  );
}
